import React from 'react';

const GeneralAssemblyTutorial: React.FC = () => {
  return (
    <div className="space-y-6">
      <section className="rounded-xl border-l-4 border-green-500 bg-gradient-to-br from-gray-50 to-gray-100 p-6 shadow-sm">
        <h3 className="mb-4 flex items-center gap-2 text-xl font-bold text-green-600">
          <span role="img" aria-label="target">🎯</span>
          Votre rôle en tant que Responsable Mutation Location
        </h3>
        <p className="mb-4 text-justify leading-relaxed text-gray-700">
          En tant que responsable mutation location, vous devez suivre et participer aux assemblées générales des copropriétés gérées par le cabinet pour :
        </p>
        
        <ul className="mb-4 ml-6 space-y-2">
          <li className="leading-relaxed text-gray-700">
            <strong>Identifier les opportunités commerciales</strong> : Repérer les propriétaires susceptibles d'avoir besoin de services de gestion locative ou de transaction
          </li>
          <li className="leading-relaxed text-gray-700">
            <strong>Distribuer les documents commerciaux</strong> : Présenter l'offre du cabinet directement aux copropriétaires
          </li>
          <li className="leading-relaxed text-gray-700">
            <strong>Créer du lien commercial</strong> : Établir un contact direct avec les propriétaires investisseurs
          </li>
          <li className="leading-relaxed text-gray-700">
            <strong>Collecter des informations terrain</strong> : Comprendre les enjeux locaux et les besoins des propriétaires
          </li>
        </ul>

        <div className="rounded-xl border-l-4 border-blue-400 bg-gradient-to-r from-blue-50 to-blue-100 p-5 font-medium text-blue-800 shadow-sm">
          <span role="img" aria-label="light bulb">💡</span> <strong>Principe clé :</strong> Les AG sont des moments privilégiés où vous rencontrez physiquement les propriétaires de biens immobiliers, créant des opportunités commerciales directes.
        </div>
      </section>

      <section className="rounded-xl border-l-4 border-green-500 bg-gradient-to-br from-gray-50 to-gray-100 p-6 shadow-sm">
        <h3 className="mb-4 flex items-center gap-2 text-xl font-bold text-green-600">
          <span role="img" aria-label="magnifying glass">🔍</span>
          Utilisation des filtres de prospection
        </h3>
        
        <h4 className="mt-5 mb-3 text-lg font-semibold text-blue-600">A - Filtre "Période AG"</h4>
        <ul className="mb-4 ml-6 space-y-2">
          <li className="leading-relaxed text-gray-700"><strong>AG du mois :</strong> Affiche les AG prévues dans le mois en cours - priorité absolue pour votre planning</li>
          <li className="leading-relaxed text-gray-700"><strong>AG du trimestre :</strong> Vision élargie sur 3 mois pour anticiper vos déplacements</li>
          <li className="leading-relaxed text-gray-700"><strong>AG mois prochain :</strong> Pour préparer votre agenda du mois suivant</li>
        </ul>

        <h4 className="mt-5 mb-3 text-lg font-semibold text-blue-600">B - Filtre "Gestionnaire"</h4>
        <p className="mb-3 text-gray-700">Sélectionnez un gestionnaire spécifique pour voir toutes ses copropriétés en AG. <strong>Utile pour :</strong></p>
        <ul className="mb-4 ml-6 space-y-2">
          <li className="leading-relaxed text-gray-700">Coordonner avec un gestionnaire en particulier</li>
          <li className="leading-relaxed text-gray-700">Voir la charge de travail par gestionnaire</li>
          <li className="leading-relaxed text-gray-700">Organiser des tournées géographiques</li>
        </ul>

        <h4 className="mt-5 mb-3 text-lg font-semibold text-blue-600">C - Filtre "État dates AG"</h4>
        <div className="my-5 rounded-xl border-2 border-green-500 bg-gradient-to-r from-green-50 to-green-100 p-5 shadow-sm">
          <h5 className="mb-3 text-lg font-bold text-green-700">
            <span role="img" aria-label="green circle">🟢</span> AG Qualifiées (avec date)
          </h5>
          <p className="text-sm leading-relaxed text-green-700">
            Ces AG ont une date précise. Vous pouvez planifier votre présence et préparer votre intervention.
          </p>
        </div>
        
        <div className="to-yellow-full my-5 rounded-xl border-2 border-yellow-500 bg-gradient-to-r from-yellow-50 p-5 shadow-sm">
          <h5 className="mb-3 text-lg font-bold text-yellow-800">
            <span role="img" aria-label="red circle">🔴</span> AG Non qualifiées (sans date)
          </h5>
          <p className="text-sm leading-relaxed text-yellow-800">
            <strong>ATTENTION :</strong> Ces AG n'ont pas de date définie. Vous devez absolument vous rapprocher du gestionnaire concerné pour connaître la date prévue, sinon vous ne pourrez pas planifier votre présence.
          </p>
        </div>

        <h4 className="mt-5 mb-3 text-lg font-semibold text-blue-600">D - Filtre "Présence AG"</h4>
        <ul className="mb-4 ml-6 space-y-2">
          <li className="leading-relaxed text-gray-700"><strong>À confirmer :</strong> Votre présence n'est pas encore décidée</li>
          <li className="leading-relaxed text-gray-700"><strong>Présent :</strong> Vous avez confirmé votre participation</li>
          <li className="leading-relaxed text-gray-700"><strong>Absent :</strong> Vous ne pourrez pas y assister</li>
        </ul>
      </section>

      <section className="rounded-xl border-l-4 border-green-500 bg-gradient-to-br from-gray-50 to-gray-100 p-6 shadow-sm">
        <h3 className="mb-4 flex items-center gap-2 text-xl font-bold text-green-600">
          <span role="img" aria-label="check mark">✅</span>
          Actions à effectuer
        </h3>
        
        <h4 className="mt-5 mb-3 text-lg font-semibold text-blue-600">1. Validation de votre présence</h4>
        <div className="mb-4 rounded-xl border-l-4 border-blue-400 bg-gradient-to-r from-blue-50 to-blue-100 p-5 font-medium text-blue-800 shadow-sm">
          Pour chaque AG, cliquez sur le bouton de présence pour indiquer si vous serez présent ou absent. Cette information permet au gestionnaire de prévoir le nombre de participants.
        </div>

        <h4 className="mt-5 mb-3 text-lg font-semibold text-blue-600">2. Coordination avec les gestionnaires</h4>
        <div className="to-yellow-full my-5 rounded-xl border-2 border-yellow-500 bg-gradient-to-r from-yellow-50 p-5 shadow-sm">
          <h5 className="mb-3 text-lg font-bold text-yellow-800">
            <span role="img" aria-label="warning">⚠️</span> PRIORITÉ ABSOLUE
          </h5>
          <p className="mb-3 text-sm leading-relaxed text-yellow-800">
            Si vous voyez des AG sans date ou avec des dates incohérentes, contactez immédiatement le gestionnaire concerné. Sans date précise, vous ne pouvez pas :
          </p>
          <ul className="ml-6 space-y-1">
            <li className="text-sm leading-relaxed text-yellow-800">Planifier votre agenda</li>
            <li className="text-sm leading-relaxed text-yellow-800">Confirmer votre présence</li>
            <li className="text-sm leading-relaxed text-yellow-800">Préparer vos documents</li>
            <li className="text-sm leading-relaxed text-yellow-800">Organiser vos déplacements</li>
          </ul>
        </div>

        <h4 className="mt-5 mb-3 text-lg font-semibold text-blue-600">3. Préparation des documents commerciaux</h4>
        <p className="mb-3 text-gray-700">En fin de chaque ligne du tableau, vous trouvez le bouton <strong>"Éditer Document AG"</strong>. Ce bouton vous permet de :</p>
        <ul className="mb-4 ml-6 space-y-2">
          <li className="leading-relaxed text-gray-700"><strong>Si vous assistez à l'AG :</strong> Préparer et imprimer les documents à distribuer directement aux copropriétaires présents</li>
          <li className="leading-relaxed text-gray-700"><strong>Si vous n'y assistez pas :</strong> Transmettre les documents au gestionnaire pour qu'il les place sur les tables ou les distribue</li>
        </ul>

        <div className="my-5 rounded-xl border-2 border-green-500 bg-gradient-to-r from-green-50 to-green-100 p-5 shadow-sm">
          <h5 className="mb-3 text-lg font-bold text-green-700">
            <span role="img" aria-label="clipboard">📋</span> Types de documents à préparer
          </h5>
          <ul className="ml-6 space-y-1">
            <li className="text-sm leading-relaxed text-green-700">Plaquettes de présentation des services gestion locative</li>
            <li className="text-sm leading-relaxed text-green-700">Offres spéciales transaction immobilière</li>
            <li className="text-sm leading-relaxed text-green-700">Cartes de visite et contacts</li>
            <li className="text-sm leading-relaxed text-green-700">Témoignages clients et références</li>
            <li className="text-sm leading-relaxed text-green-700">Grilles tarifaires adaptées aux investisseurs</li>
          </ul>
        </div>
      </section>

      <section className="rounded-xl border-l-4 border-green-500 bg-gradient-to-br from-gray-50 to-gray-100 p-6 shadow-sm">
        <h3 className="mb-4 flex items-center gap-2 text-xl font-bold text-green-600">
          <span role="img" aria-label="chart">📊</span>
          Optimisation de votre prospection
        </h3>
        
        <h4 className="mt-5 mb-3 text-lg font-semibold text-blue-600">Stratégie par niveau de ventes</h4>
        <p className="mb-3 text-gray-700">Le tableau indique le nombre de ventes par copropriété avec un code couleur :</p>
        <ul className="mb-4 ml-6 space-y-2">
          <li className="leading-relaxed text-gray-700"><strong><span role="img" aria-label="green circle">🟢</span> +10 ventes (24 mois) :</strong> Copropriétés très dynamiques, forte probabilité d'investisseurs actifs</li>
          <li className="leading-relaxed text-gray-700"><strong><span role="img" aria-label="orange circle">🟠</span> -3 ventes (24 mois) :</strong> Marché modéré, cibler les propriétaires pour la gestion locative</li>
          <li className="leading-relaxed text-gray-700"><strong><span role="img" aria-label="red circle">🔴</span> Pas de vente (24 mois) :</strong> Marché atone, focus sur l'accompagnement et les services</li>
        </ul>

        <div className="mb-4 rounded-xl border-l-4 border-blue-400 bg-gradient-to-r from-blue-50 to-blue-100 p-5 font-medium text-blue-800 shadow-sm">
          <span role="img" aria-label="money">💰</span> <strong>Conseil commercial :</strong> Priorisez votre présence aux AG des copropriétés avec le plus de ventes - c'est là que vous trouverez le plus d'investisseurs actifs !
        </div>

        <h4 className="mt-5 mb-3 text-lg font-semibold text-blue-600">Planification géographique</h4>
        <p className="text-gray-700">Utilisez les filtres pour organiser des "tournées d'AG" par zone géographique et optimiser vos déplacements.</p>
      </section>

      <section className="rounded-xl border-l-4 border-green-500 bg-gradient-to-br from-gray-50 to-gray-100 p-6 shadow-sm">
        <h3 className="mb-4 flex items-center gap-2 text-xl font-bold text-green-600">
          <span role="img" aria-label="rocket">🚀</span>
          Bonnes pratiques
        </h3>
        <ul className="ml-6 space-y-2">
          <li className="leading-relaxed text-gray-700"><strong>Vérifiez régulièrement</strong> les dates d'AG et votre statut de présence</li>
          <li className="leading-relaxed text-gray-700"><strong>Préparez vos documents en avance</strong> pour chaque AG où vous serez présent</li>
          <li className="leading-relaxed text-gray-700"><strong>Coordonnez-vous avec les gestionnaires</strong> pour les AG où vous serez absent</li>
          <li className="leading-relaxed text-gray-700"><strong>Utilisez l'export Excel</strong> pour créer vos plannings personnalisés</li>
          <li className="leading-relaxed text-gray-700"><strong>Priorisez les copropriétés</strong> avec beaucoup de transactions (potentiel client élevé)</li>
          <li className="leading-relaxed text-gray-700"><strong>Suivez vos résultats</strong> : nombre d'AG assistées, contacts générés, contrats signés</li>
        </ul>
      </section>
    </div>
  );
};

export default GeneralAssemblyTutorial;
